/**
 * The membership next-charge billing block (DNKADM-0374), extracted verbatim from
 * `MembershipSummaryBar` so a second surface — the member's Payments tab (DNKADM-0391) —
 * can render the same reason-specific copy without duplicating it. The summary bar keeps
 * rendering exactly what it rendered before the extraction.
 *
 * Renders reason-specific copy, a date ("on or about {date}"), and an estimated amount
 * when present. Hidden entirely when `reason === 'no_membership'` (callers guard this).
 *
 * An ending membership (DNKADM-0476 F-06, pair of DNKAPI-0692): `cancel_scheduled` or `canceled`
 * WITH a `nextBillingOn` reads "Ends {date}" and nothing else. The date is the last day, not a
 * renewal, so no "on or about" and no amount (the amount is the last period's estimate, not an
 * upcoming charge). Without a date the reason's own line stays.
 */
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { MembershipNextCharge } from '@/api/membershipNextCharge';
import { formatApiDate } from '@/components/list/listDate';
import { inkAlpha } from '@/theme';

function formatMoney(amount: number, currency: string | null): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency ?? 'USD',
  }).format(amount);
}

export function NextChargeBillingBlock({
  data,
  theme,
  topGap = 0.75,
}: {
  data: MembershipNextCharge;
  theme: Theme;
  /**
   * Gap between this block and the label above it. Defaults to the `0.75` the
   * summary bar has always rendered, so that surface is untouched; the Payments
   * tab's Upcoming card passes a tighter value so its label and value sit close
   * enough to read as one pair (DNKADM-0393 U4).
   */
  topGap?: number;
}) {
  const { reason, nextBillingOn, amount, currency, pauseEffectiveOn } = data;

  let headline: string | null = null;
  let showDate = false;

  switch (reason) {
    case 'not_available':
      // DNKADM-0393 U4 — "No scheduled charge date" is said ONLY when the read
      // truly carries no date. This branch used to assert it unconditionally and
      // set `showDate = false`, so a `not_available` response that DID carry a
      // `nextBillingOn` had its date thrown away and was reported to the operator
      // as having none. That contradicts this read's own DNKAPI-0531 addendum —
      // "a brand-new anchored subscription MAY have a nextBillingOn date (its
      // first full-plan charge). Show it." The date now wins whenever there is one.
      if (nextBillingOn !== null) {
        showDate = true;
        break;
      }
      headline = 'No scheduled charge date';
      showDate = false;
      break;
    case 'canceled':
      headline = nextBillingOn !== null ? `Ends ${formatApiDate(nextBillingOn)}` : 'Cancelled';
      break;
    case 'cancel_scheduled':
      headline =
        nextBillingOn !== null
          ? `Ends ${formatApiDate(nextBillingOn)}`
          : 'Ends at period end — one final charge may still apply';
      break;
    case 'paused':
      headline = 'Paused';
      break;
    case null:
    default:
      // null or unknown reason with a date → show the date
      showDate = true;
      break;
  }

  const hasDate = nextBillingOn !== null;
  const hasAmount = amount !== null && currency !== null;

  return (
    <Box sx={{ mt: topGap, fontSize: 13, color: inkAlpha(theme, 0.62) }}>
      {headline && (
        <Typography component="span" sx={{ fontSize: 13 }}>
          {headline}
          {/* Show amount alongside not_available if present */}
          {reason === 'not_available' && hasAmount && (
            <>{' '}· {formatMoney(amount!, currency!)} est.</>
          )}
        </Typography>
      )}
      {(showDate || (!headline && hasDate)) && hasDate && (
        <Typography component="span" sx={{ fontSize: 13 }}>
          on or about {formatApiDate(nextBillingOn!)}
          {hasAmount && <>{' '}· {formatMoney(amount!, currency!)} est.</>}
        </Typography>
      )}
      {pauseEffectiveOn !== null && reason !== 'paused' && (
        <Typography component="span" sx={{ fontSize: 13, display: 'block' }}>
          Pausing on {formatApiDate(pauseEffectiveOn)}
        </Typography>
      )}
    </Box>
  );
}
